import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Loader2, ExternalLink, Sparkles, Inbox } from "lucide-react";
import api, { apiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Card } from "../components/ui/card";
import { Badge } from "../components/ui/badge";

const MATCH_STYLE = {
  "Strong Match": "bg-emerald-100 text-emerald-700 border-emerald-200",
  "Potential Match": "bg-sky-100 text-sky-700 border-sky-200",
  "Weak Match": "bg-amber-100 text-amber-700 border-amber-200",
  "Does Not Match": "bg-red-100 text-red-700 border-red-200",
};
const money = (v) => (v || v === 0 ? `$${Number(v).toLocaleString()}` : "—");

export default function BuyBoxSubmissions() {
  const { id } = useParams();
  const nav = useNavigate();
  const [items, setItems] = useState([]);
  const [bb, setBb] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      const [subs, box] = await Promise.all([
        api.get(`/buybox/${id}/submissions`),
        api.get(`/buybox/${id}`),
      ]);
      setItems(subs.data.items || []);
      setBb(box.data);
    } catch (e) { toast.error(apiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); api.post(`/buybox/${id}/submissions/mark-viewed`).catch(() => {}); }, [id]);

  return (
    <div className="p-6 md:p-8 max-w-5xl mx-auto" data-testid="buy-box-submissions-page">
      <div className="flex items-center gap-3 mb-6">
        <Button variant="ghost" size="icon" onClick={() => nav("/buybox")}><ArrowLeft className="h-4 w-4" /></Button>
        <div>
          <h1 className="text-2xl md:text-3xl font-semibold tracking-tight">Submissions</h1>
          <p className="text-muted-foreground text-sm">{bb?.name} · {items.length} received</p>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-24 text-muted-foreground"><Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading…</div>
      ) : items.length === 0 ? (
        <Card className="p-12 text-center border-dashed">
          <Inbox className="h-10 w-10 mx-auto text-muted-foreground/50 mb-3" />
          <h3 className="text-lg font-medium">No submissions yet</h3>
          <p className="text-muted-foreground text-sm mt-1">Share your public link with wholesalers and agents to start receiving deals.</p>
        </Card>
      ) : (
        <div className="space-y-3">
          {items.map((s) => {
            const m = s.buybox_match || {};
            const scoring = m.status === "scoring";
            return (
              <Card key={s.id} className="p-5" data-testid={`submission-${s.id}`}>
                <div className="flex items-start justify-between gap-4">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <h3 className="font-semibold truncate">{s.property_name || s.street_address || "Property"}</h3>
                      <Badge variant="outline" className="text-xs">{s.property_source}</Badge>
                    </div>
                    <p className="text-sm text-muted-foreground mt-0.5">
                      {[s.city, s.state, s.postal_code].filter(Boolean).join(", ") || "—"}
                      {s.apn ? ` · APN ${s.apn}` : ""}
                    </p>
                    <div className="flex flex-wrap gap-x-5 gap-y-1 mt-2 text-sm">
                      <span><span className="text-muted-foreground">Ask:</span> {money(s.list_price)}</span>
                      <span><span className="text-muted-foreground">Lot:</span> {s.lot_size_sqft ? `${Number(s.lot_size_sqft).toLocaleString()} SF` : "—"}</span>
                      <span><span className="text-muted-foreground">From:</span> {s.submitter_name || "—"} ({s.submitter_email || "no email"})</span>
                    </div>
                  </div>
                  <div className="text-right shrink-0">
                    {scoring ? (
                      <span className="inline-flex items-center gap-1.5 text-xs text-muted-foreground"><Loader2 className="h-3.5 w-3.5 animate-spin" /> Scoring…</span>
                    ) : (
                      <>
                        {typeof m.score === "number" && <div className="text-2xl font-semibold">{m.score}%</div>}
                        {m.classification && <Badge variant="outline" className={`${MATCH_STYLE[m.classification]} text-xs`}>{m.classification}</Badge>}
                      </>
                    )}
                  </div>
                </div>

                {!scoring && (m.summary || (m.reasons || []).length > 0 || (m.risks || []).length > 0) && (
                  <div className="mt-3 rounded-lg bg-muted/50 p-3 text-sm space-y-2">
                    {m.summary && <p className="flex items-start gap-1.5"><Sparkles className="h-4 w-4 text-primary mt-0.5 shrink-0" /> {m.summary}</p>}
                    {(m.reasons || []).length > 0 && (
                      <div><span className="text-xs font-medium text-emerald-700">Why it fits</span>
                        <ul className="list-disc list-inside text-muted-foreground">{m.reasons.map((r, i) => <li key={i}>{r}</li>)}</ul>
                      </div>
                    )}
                    {(m.risks || []).length > 0 && (
                      <div><span className="text-xs font-medium text-amber-700">Verify / risks</span>
                        <ul className="list-disc list-inside text-muted-foreground">{m.risks.map((r, i) => <li key={i}>{r}</li>)}</ul>
                      </div>
                    )}
                  </div>
                )}

                <div className="mt-3 flex justify-end">
                  <Button size="sm" variant="outline" data-testid={`open-record-${s.id}`} onClick={() => nav(`/record/property_search/${s.id}`)}>
                    Open in Property Screening <ExternalLink className="h-3.5 w-3.5 ml-1.5" />
                  </Button>
                </div>
              </Card>
            );
          })}
        </div>
      )}

      {items.some((s) => (s.buybox_match || {}).status === "scoring") && (
        <div className="text-center mt-4">
          <Button variant="ghost" size="sm" onClick={load}>Refresh scores</Button>
        </div>
      )}
    </div>
  );
}
